/**
 * RTWO Indexer Engine — Storage Binding Service
 */

import { INDEXER_SERVICE_IDS, INDEX_TYPE_REGISTRY } from '../domain-models';
import type { RTWOIndexStorageBinding, RTWOIndexType, StorageBindingId } from '../domain-models';
import type { IIndexStorageBindingService } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class IndexStorageBindingService
  extends BaseIndexerService
  implements IIndexStorageBindingService
{
  readonly serviceId = INDEXER_SERVICE_IDS.STORAGE_BINDING;
  readonly serviceName = 'Index Storage Binding Service';
  readonly description =
    'Manages architecture-defined storage bindings. All bindings not configured.';
  readonly integrations = ['Core Engine', 'Storage Abstraction Layer', 'System Registry'];
  readonly requiresRPCSource = false;
  readonly requiresIndexStorage = true;

  private readonly architectureBindings: RTWOIndexStorageBinding[];

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories
  ) {
    super(context);
    this.architectureBindings = INDEX_TYPE_REGISTRY.map((desc, index) => ({
      bindingId: `binding-${index + 1}`,
      indexType: desc.indexType,
      storageProvider: 'not_configured' as const,
      status: 'not_configured' as const,
      createdAt: null,
    }));
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_index_storage';
    for (const binding of this.architectureBindings) {
      this.repositories.storageBindings.save(binding);
    }
  }

  getAllBindings(): RTWOIndexStorageBinding[] {
    return this.architectureBindings;
  }

  getBinding(bindingId: StorageBindingId): RTWOIndexStorageBinding | null {
    return this.architectureBindings.find((binding) => binding.bindingId === bindingId) ?? null;
  }

  getBindingByIndexType(indexType: RTWOIndexType): RTWOIndexStorageBinding | null {
    return this.architectureBindings.find((binding) => binding.indexType === indexType) ?? null;
  }

  getConfiguredBindingCount(): 0 {
    return 0;
  }
}
